/**
 * フロントエンドエラーレポーター
 *
 * グローバルエラーハンドラ（unhandledrejection, onerror）を登録し、
 * エラー発生時にリングバッファの内容と一緒にバックエンドへ送信する。
 *
 * 設計方針:
 * - エラーレポート自体が二次的なエラーを起こしてはならない
 * - レート制限で過剰な送信を防ぐ（同一メッセージ60秒、全体5件/分）
 * - fire-and-forget: レスポンスを待たない、リトライしない
 */
import { logBuffer, type RingBufferEntry } from './logger'
import { useAuthStore } from '../stores/authStore'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
const MAX_PAYLOAD_BYTES = 64 * 1024
const COOLDOWN_MS = 60_000
const MAX_REPORTS_PER_MINUTE = 5

interface ErrorReport {
  error: {
    message: string
    stack: string | undefined
    type: string
  }
  context: {
    url: string
    userAgent: string
    userId: string | undefined
    organizationId: string | undefined
    timestamp: string
  }
  recentLogs: RingBufferEntry[]
}

// レート制限用の状態
const recentMessages = new Map<string, number>()
let reportCountThisMinute = 0
let minuteResetTimer: ReturnType<typeof setTimeout> | null = null

function isRateLimited(message: string): boolean {
  const now = Date.now()

  // 同一メッセージのクールダウン
  const lastSent = recentMessages.get(message)
  if (lastSent && now - lastSent < COOLDOWN_MS) return true

  // 全体レート制限
  if (reportCountThisMinute >= MAX_REPORTS_PER_MINUTE) return true

  return false
}

function recordSent(message: string): void {
  recentMessages.set(message, Date.now())
  reportCountThisMinute++

  if (!minuteResetTimer) {
    minuteResetTimer = setTimeout(() => {
      reportCountThisMinute = 0
      minuteResetTimer = null
    }, COOLDOWN_MS)
  }
}

function getUserContext(): { userId: string | undefined; organizationId: string | undefined } {
  try {
    const state = useAuthStore.getState()
    return {
      userId: state.user?.sub,
      organizationId: state.user?.organizationId,
    }
  } catch {
    return { userId: undefined, organizationId: undefined }
  }
}

function getAuthToken(): string | null {
  try {
    const state = useAuthStore.getState()
    return (state as any).tokens?.idToken ?? null
  } catch {
    return null
  }
}

function sendReport(report: ErrorReport): void {
  try {
    let body = JSON.stringify(report)
    if (body.length > MAX_PAYLOAD_BYTES) {
      // ログを削って再試行
      report.recentLogs = report.recentLogs.slice(-20)
      body = JSON.stringify(report)
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    fetch(`${API_BASE_URL}/api/error-reports`, {
      method: 'POST',
      headers,
      body,
    }).catch(() => {
      // 送信失敗は無視
    })

    logBuffer.clear()
  } catch {
    // エラーレポーター自体のエラーは無視
  }
}

/**
 * エラーをレポートする（外部からも呼び出し可能）
 */
export function reportError(error: Error | string, type: string): void {
  try {
    const message = typeof error === 'string' ? error : error.message
    const stack = typeof error === 'string' ? undefined : error.stack

    if (isRateLimited(message)) return
    recordSent(message)

    const { userId, organizationId } = getUserContext()

    const report: ErrorReport = {
      error: { message, stack, type },
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId,
        organizationId,
        timestamp: new Date().toISOString(),
      },
      recentLogs: logBuffer.getEntries(),
    }

    sendReport(report)
  } catch {
    // 絶対に例外を投げない
  }
}

/**
 * グローバルエラーハンドラを登録する
 * main.tsx から React レンダリング前に1回呼び出す
 */
export function initErrorReporter(): void {
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : String(event.reason)
    reportError(error, 'unhandledrejection')
  })

  window.onerror = (message, _source, _lineno, _colno, error) => {
    reportError(error ?? String(message), 'onerror')
  }
}
